import { computed, makeObservable, observable, action, reaction, runInAction } from "mobx";
import { groupLens, LensGroup } from "@/modules/timeline";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { MDSIconKind, MDSMenuContentItemKind, MDSMenuContentList } from "@/mds";
import { ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { actions } from "@/actions";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { DeleteSharedNotesModalStore } from "@/components/modal/delete-shared-notes";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import localDb from "@/domains/local-db";
import { liveQuery, Subscription } from "dexie";
import {
  getItemListRowForNote,
  getItemListRowForCalendarEvent,
} from "@/components/item-list/helpers";
import { EventContext } from "@/domains/metrics/context";
import { getLocalTimeZone } from "@internationalized/date";
import { INotesListPage } from "@/store/pages/NotesListPageStore/types";
import { INoteObservable, NotesIndexTuple } from "@/store/note";
import { isReminderShillFeatureFlagEnabled } from "@/store/note/reminder-shill-feature-flag";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { SortByKind } from "@/modules/lenses/types";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { itemsModule } from "@/modules/items";
import { DateTime } from "luxon";
import { take } from "lodash-es";
import { NotesListPageListUpcomingEventsHeaderButtons } from "@/pages/notes/components/notes-list-page-list-upcoming-events-header-buttons";
import { CalendarIntegrationNotConnectedContent } from "@/components/item-list/rows/other-variant-content/calendar-integration-not-connected-content";
import { NoUpcomingMeetingsContent } from "@/components/item-list/rows/other-variant-content/no-upcoming-meetings-content";
import { CalendarDisconnectedContent } from "@/components/item-list/rows/other-variant-content/calendar-disconnected-content";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { getOngoingAndUpcomingCalendarEvents } from "@/store/calendar/space-account-calendar-event/search-calendar-events";
import {
  calculateUpcomingEventsCutoffDate,
  getCutoffDayName,
} from "@/domains/calendar/calculateUpcomingEventsCutoffDate";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { UpdateSpaceAccountCalendarEventOperation } from "@/store/sync/operations/calendars/UpdateSpaceAccountCalendarEventOperation";

/** Maximum number of upcoming events to show in the notes list. Matches iOS. */
const MAX_UPCOMING_EVENTS_IN_LIST = 2;

export class NotesListPageStore extends AppSubStore implements INotesListPage {
  tabId: string;
  importBatchId: Maybe<string> = undefined;
  supportsAddToCollectionListAction = true;
  supportsCopyNoteAsMarkdownListAction = true;
  supportsMoveNoteToTrashListAction = true;
  supportsChatListAction = true;
  listState: ListStateObservable;
  searchQuery = "";
  addToCollectionModal: AddToCollectionModalStore;
  deleteSharedNotesModal: DeleteSharedNotesModalStore;
  shareSheetModal: ShareSheetModalStore;
  isNotesLoading: boolean;
  isCalendarEventsLoading: boolean;
  subscriptionInitialized: boolean;
  liveQuerySubscription?: Subscription;
  subscribedNoteIndexTuples: NotesIndexTuple[] = [];
  hasCalendarConnections: boolean = false;
  calendarEventsSubscription?: Subscription;
  subscribedCalendarEventIds: Uuid[] = [];
  notesWithSources: Uuid[] = [];
  isUpcomingEventsCollapsed: boolean = false;
  noteIdsLinkedToUpcomingEvents: Set<Uuid> = new Set();
  calendarQueryStartTime: DateTime;

  eventContext = EventContext.NotesMultiselectActions;

  filters: NotesListFilter;

  // allows to skip unnecessary reactions
  private initialized: boolean = false;

  // ref to current scroll position of virtualized list
  private currentScrollTop = 0;

  private readonly supportedSortOptions: SortByKind[] = [
    SortByKind.LastModified,
    SortByKind.LastCreated,
    SortByKind.LastViewed,
    SortByKind.LastShared,
  ];

  constructor(injectedDeps: AppSubStoreArgs & { tabId: string }) {
    super(injectedDeps);

    this.tabId = injectedDeps.tabId;
    this.addToCollectionModal = new AddToCollectionModalStore(injectedDeps, {});
    this.deleteSharedNotesModal = new DeleteSharedNotesModalStore(injectedDeps, {});
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.listState = new ListStateObservable({ ...injectedDeps, listStateProvider: this });
    this.filters = new NotesListFilter(this.store, this.supportedSortOptions);

    this.isNotesLoading = true;
    this.isCalendarEventsLoading = true;

    this.subscriptionInitialized = false;
    this.currentScrollTop = 0;
    this.calendarQueryStartTime = DateTime.now();

    makeObservable<
      this,
      | "generateItemDropdown"
      | "generateCalendarEventDropdown"
      | "runLiveQuery"
      | "supportedSortOptions"
      | "initialized"
      | "currentScrollTop"
      | "importBatchId"
      | "subscribeToCalendarEvents"
      | "calendarEventsSubscription"
    >(this, {
      supportsChatListAction: true,
      tabId: true,
      useEffects: false,
      supportedSortOptions: true,
      runLiveQuery: action,
      liveQuerySubscription: observable,
      subscriptionInitialized: observable,

      isNotesLoading: observable,
      isCalendarEventsLoading: observable,
      isLoading: computed,

      subscribedNoteIndexTuples: observable,
      subscribedCalendarEventIds: observable,
      hasCalendarConnections: observable,
      notesWithSources: observable,
      isUpcomingEventsCollapsed: observable,
      groupedLensItems: computed,
      itemRows: computed,

      supportsAddToCollectionListAction: observable,
      supportsCopyNoteAsMarkdownListAction: observable,
      supportsMoveNoteToTrashListAction: observable,
      searchQuery: observable,
      listState: false,
      addToCollectionModal: false,
      deleteSharedNotesModal: false,
      shareSheetModal: false,
      eventContext: false,
      importBatchId: observable,
      setImportBatchId: action,

      generateItemDropdown: false,
      generateCalendarEventDropdown: false,
      orderedItemIds: computed,
      hasNoItems: computed,

      initialize: action,
      filters: false,
      initialized: false,
      subscribeToCalendarEvents: false,
      calendarEventsSubscription: observable,
      noteIdsLinkedToUpcomingEvents: observable,
      calendarQueryStartTime: observable,

      // virtualized list index
      currentScrollTop: observable,
      scrollTop: computed,
      setScrollTop: action,
      setUpcomingEventsCollapsed: action,
    });

    reaction(
      () => this.filters.params,
      (params, previousParams) => {
        this.store.memDb.settings.setNotesListPageParams(this.filters.params);

        if (
          params.lens !== previousParams.lens ||
          params.sortBy !== previousParams.sortBy ||
          params.filter !== previousParams.filter
        ) {
          this.setScrollTop(0);
        }
      }
    );

    reaction(
      () => this.subscriptionInitialized,
      initialized => {
        if (initialized) setTimeout(() => runInAction(() => (this.isNotesLoading = false)), 100);
      }
    );

    reaction(
      () => this.isUpcomingEventsCollapsed,
      () => {
        this.store.memDb.settings.setNotesListPageIsUpcomingEventsCollapsed(
          this.isUpcomingEventsCollapsed
        );
      }
    );
  }

  get groupedLensItems(): LensGroup<NotesIndexTuple>[] {
    return groupLens(
      this.subscribedNoteIndexTuples.map(tuple => ({
        dateTime: new Date(tuple.length === 4 ? tuple[2] : tuple[1]),
        item: tuple,
      }))
    );
  }

  get scrollTop() {
    return this.currentScrollTop;
  }

  get itemRows(): ItemListRowData[] {
    const output: ItemListRowData[] = [];

    const hideCalendarIntegrationShill =
      this.store.spaceAccountSettings.calendar.hideCalendarIntegrationShill;

    const shouldShowUpcomingMeetingsSection = (() => {
      // NOTE: Calendar evetns are irrelevant when viewing import batch.
      if (this.importBatchId) return false;
      if (this.hasCalendarConnections) return true;
      return !hideCalendarIntegrationShill;
    })();

    if (shouldShowUpcomingMeetingsSection) {
      output.push({
        type: ItemListRowType.CollapsibleSectionHeader,
        key: "upcoming-meetings",
        payload: {
          title: "Upcoming Meetings",
          isCollapsed: this.isUpcomingEventsCollapsed,
          onCollapseChange: this.setUpcomingEventsCollapsed,
          rightAddon: <NotesListPageListUpcomingEventsHeaderButtons />,
        },
      });

      if (!this.isUpcomingEventsCollapsed) {
        if (this.hasCalendarConnections) {
          if (this.subscribedCalendarEventIds.length > 0) {
            const eventsToShow = this.subscribedCalendarEventIds;
            for (const calendarEventId of eventsToShow) {
              output.push({
                type: ItemListRowType.AsyncCalendarEvent,
                key: calendarEventId,
                payload: {
                  calendarEventId: calendarEventId,
                  itemRow: event =>
                    getItemListRowForCalendarEvent({
                      listState: this.listState,
                      calendarEventObservable: event,
                      store: this.store,
                      dropdown: this.generateCalendarEventDropdown({
                        calendarEventObservable: event,
                      }),
                    }),
                },
              });
            }
          } else {
            const hasProblematicCalendars =
              this.store.spaceAccountCalendars.failedOrRetryingCalendars.length > 0;

            if (hasProblematicCalendars) {
              output.push({
                type: ItemListRowType.Other,
                key: "calendar-disconnected",
                payload: {
                  children: <CalendarDisconnectedContent />,
                },
              });
            } else {
              const cutoffDate = calculateUpcomingEventsCutoffDate({ startDate: DateTime.now() });
              const cutoffDayName = getCutoffDayName({ cutoffDateTime: cutoffDate });
              const connectedCalendarsCount = this.store.spaceAccountCalendarConnections.size; // .size returns -1 if not loaded
              if (connectedCalendarsCount >= 0) {
                output.push({
                  type: ItemListRowType.Other,
                  key: "no-upcoming-meetings",
                  payload: {
                    children: (
                      <NoUpcomingMeetingsContent
                        cutoffDayName={cutoffDayName}
                        connectedCalendarsCount={connectedCalendarsCount}
                      />
                    ),
                  },
                });
              } else {
                output.push({
                  type: ItemListRowType.Skeleton,
                  key: "no-upcoming-meetings",
                  payload: {},
                });
              }
            }
          }
        } else {
          output.push({
            type: ItemListRowType.Other,
            key: "calendar-integration-not-connected",
            payload: {
              children: <CalendarIntegrationNotConnectedContent />,
            },
          });
        }
      }
    }

    // Add notes sections
    for (const group of this.groupedLensItems) {
      // Filter out notes that are linked to upcoming calendar events (they're shown in the events section)
      const filteredItems = group.items.filter(item => {
        const tuple = item.item;
        const itemId = tuple.length === 4 ? tuple[3] : tuple[2];
        return !this.noteIdsLinkedToUpcomingEvents.has(itemId);
      });

      // Skip the group if all items were filtered out
      if (filteredItems.length === 0) continue;

      output.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const item of filteredItems) {
        const tuple = item.item;
        const itemId = tuple.length === 4 ? tuple[3] : tuple[2];
        const itemAppearance = itemsModule.resolveAppearance({ itemId, appStore: this.store });

        output.push({
          type: ItemListRowType.AsyncNote,
          key: itemId,
          payload: {
            noteId: itemId,
            itemRow: note =>
              getItemListRowForNote({
                dateTime: item.dateTime,
                dropdown: this.generateItemDropdown({ noteObservable: note }),
                inMainPanel: true,
                listState: this.listState,
                noteObservable: note,
                showReminderShill: isReminderShillFeatureFlagEnabled({
                  getFlagStatus: flag => this.store.spaceAccountFeatureFlags.getFlagStatus(flag),
                }),
                store: this.store,
                tabId: this.tabId,
                appearance: itemAppearance,
              }),
          },
        });
      }
    }

    if (output.length) {
      output.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return output;
  }

  // TODO: intro named type of tuples, indexes are not safe nor readable
  get orderedItemIds() {
    return this.subscribedNoteIndexTuples.map(tuple => {
      if (tuple.length === 4) {
        return tuple[3];
      }

      return tuple[2];
    });
  }

  get hasNoItems() {
    return (
      !this.isLoading &&
      this.subscribedNoteIndexTuples.length === 0 &&
      this.subscribedCalendarEventIds.length === 0
    );
  }

  get isLoading() {
    return this.isNotesLoading || this.isCalendarEventsLoading;
  }

  setScrollTop = (value: number) => {
    this.currentScrollTop = value;
  };

  setUpcomingEventsCollapsed = (collapsed: boolean) => {
    this.isUpcomingEventsCollapsed = collapsed;
  };

  // Initialization
  async initialize() {
    if (this.initialized) return;

    this.initialized = true;

    const [localDBParams, memDbParams, isUpcomingEventsCollapsed] = await Promise.all([
      localDb.settings.getNotesListPageParams(),
      this.store.memDb.settings.getNotesListPageParams(),
      this.store.memDb.settings.getNotesListPageIsUpcomingEventsCollapsed(),
    ]);

    if (localDBParams?.sortBy && this.store.memDb) {
      await this.store.memDb.settings.setNotesListPageParams(localDBParams);
      await localDb.settings.removeNotesListPageParams();
    }

    this.filters.setParams({
      lens: localDBParams?.lens ?? memDbParams?.lens ?? NotesListFilter.DEFAULT_LENS,
      sortBy: localDBParams?.sortBy ?? memDbParams?.sortBy ?? NotesListFilter.DEFAULT_SORT_BY,
      filter: localDBParams?.filter ?? memDbParams?.filter,
    });

    runInAction(() => {
      this.isUpcomingEventsCollapsed = isUpcomingEventsCollapsed;
    });
  }

  private runLiveQuery() {
    return reaction(
      () => ({
        ...this.filters.params,
        importBatchId: this.importBatchId,
        activeAudioCaptureNoteId: this.store.audio.activeRecordingNoteId,
        notesWithSources: this.notesWithSources,
      }),
      ({ sortBy, lens, limit, filter, importBatchId }) => {
        this.liveQuerySubscription?.unsubscribe();
        this.liveQuerySubscription = liveQuery(() => {
          const filterOption = NotesListFilter.dateFilterMap[sortBy];
          const dateRange = this.filters.dateSelectedRange;

          return this.store.notes.search({
            lens,
            sortBy,
            limit,
            filter: {
              ...filter,
              [filterOption]: {
                from: dateRange?.start?.toDate(getLocalTimeZone()).toString(),
                to: dateRange?.end?.add({ days: 1 }).toDate(getLocalTimeZone()).toString(),
                olderThan: filter?.date?.olderThan,
              },
              collections: filter?.collections ? [filter.collections] : undefined,
            },
            importBatchId,
          });
        }).subscribe({
          next: rows => {
            runInAction(() => {
              this.subscribedNoteIndexTuples = rows;
              this.subscriptionInitialized = true;
              if (this.initialized) this.isNotesLoading = false;
            });
          },
        });
      },
      { fireImmediately: true }
    );
  }

  /**
   * Subscribes to calendar events to be displayed in the upcoming events section. Heuristics:
   * - Maximum of 2 ongoing or upcoming events
   * - Ordered by: start_time, end_time, alphabetically by title, id
   * - Filter out events with 0 attendees unless enabled, and 1 attendee without a meeting link
   * - Filter out events that the user has declined
   */
  private subscribeToCalendarEvents() {
    this.calendarEventsSubscription?.unsubscribe();

    // NOTE: Calendar events are irrelevant when viewing import batch
    if (this.importBatchId) {
      this.isCalendarEventsLoading = false;
      return;
    }

    const startTime = this.calendarQueryStartTime;
    this.calendarEventsSubscription = liveQuery(async () => {
      const { ongoingEventIds, upcomingEventIds } = await getOngoingAndUpcomingCalendarEvents({
        startTime,
        store: this.store,
        includeHidden: false,
      });

      const numCalendarConnections =
        await this.store.spaceAccountCalendarConnections.localTable.count();
      const hasCalendarConnections = numCalendarConnections > 0;
      const allEventIds = [...ongoingEventIds, ...upcomingEventIds];
      const shownEventIds = take(allEventIds, MAX_UPCOMING_EVENTS_IN_LIST);

      // Find note IDs linked to these calendar events
      const noteIdsLinkedToEvents = new Set<Uuid>();
      if (shownEventIds.length > 0) {
        const events =
          await this.store.spaceAccountCalendarEvents.localTable.bulkGet(shownEventIds);

        const sourceIds = events
          .filter((event): event is NonNullable<typeof event> => event != null)
          .map(event => event.model_data?.associated_connected_calendar_event_id)
          .filter((id): id is string => Boolean(id))
          .map(connectedCalendarEventId =>
            resolveSourceIdForCalendarEvent({ connectedCalendarEventId })
          );

        if (sourceIds.length > 0) {
          // Find notes linked to these sources
          const noteSources = await this.store.noteSources.localTable
            .where("source_id")
            .anyOf(sourceIds)
            .toArray();

          for (const noteSource of noteSources) {
            const noteId = noteSource.model_data?.note_id;
            if (noteId) noteIdsLinkedToEvents.add(noteId);
          }
        }
      }

      return {
        shownEventIds,
        noteIdsLinkedToEvents,
        hasCalendarConnections,
      };
    }).subscribe({
      next: ({ shownEventIds, noteIdsLinkedToEvents, hasCalendarConnections }) => {
        runInAction(() => {
          this.subscribedCalendarEventIds = shownEventIds;
          this.noteIdsLinkedToUpcomingEvents = noteIdsLinkedToEvents;
          this.hasCalendarConnections = hasCalendarConnections;
          if (this.initialized) this.isCalendarEventsLoading = false;
        });
      },
      error: (error: Error) => {
        logger.error({
          message: "[NotesListPageStore] Error in calendar events subscription",
          info: { err: objectModule.safeErrorAsJson(error) },
        });
        runInAction(() => {
          this.subscribedCalendarEventIds = [];
          this.noteIdsLinkedToUpcomingEvents = new Set();
          this.hasCalendarConnections = false;
          this.isCalendarEventsLoading = false;
        });
      },
    });
  }

  private generateItemDropdown({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `share-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Share,
        label: "Share",
        onClick: () =>
          this.shareSheetModal.open({
            id: noteObservable.id,
            entityKind: ShareSheetEntityKind.Note,
            eventContext: EventContext.NotesRowActions,
          }),
      },
      {
        id: `copy-link-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Copy,
        label: "Copy link",
        onClick: () => actions.copyNoteLinkToClipboard({ noteId: noteObservable.id }),
      },
      {
        id: `copy-as-markdown-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Text,
        label: "Copy as Markdown",
        onClick: () =>
          actions.copyNotesAsMarkdown({ noteIds: [noteObservable.id], store: this.store }),
      },
      {
        id: "divider-1",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `favorite-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: noteObservable.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack,
        label: noteObservable.isFavorited ? "Unpin" : "Pin",
        onClick: async () => await noteObservable.toggleFavorite(),
      },
      {
        id: `add-to-collection-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Collection,
        label: "Organize",
        onClick: () =>
          this.addToCollectionModal.open({
            noteIds: [noteObservable.id],
            eventContext: EventContext.NotesRowActions,
          }),
      },
      {
        id: "divider-2",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `move-to-trash-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Trash,
        label: "Trash",
        onClick: async () => {
          if (noteObservable.isShared) {
            this.deleteSharedNotesModal.open(noteObservable);
          } else {
            await noteObservable.moveToTrash();
          }
        },
      },
    ];
  }

  private generateCalendarEventDropdown({
    calendarEventObservable,
  }: {
    calendarEventObservable: SpaceAccountCalendarEventObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `hide-meeting-${calendarEventObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.EyeSlash,
        label: "Hide meeting",
        onClick: async () => {
          await new UpdateSpaceAccountCalendarEventOperation({
            store: this.store,
            payload: {
              id: calendarEventObservable.id,
              is_hidden: true,
            },
          }).execute();
          toastModule.triggerToast({ content: "Meeting hidden" });
        },
      },
    ];
  }

  setImportBatchId(importBatchId: string) {
    this.importBatchId = importBatchId;
  }

  useEffects() {
    useEffectOnMount(() => {
      const reactionDisposer = this.runLiveQuery();
      this.subscribeToCalendarEvents();

      const abortController = new AbortController();
      let minuteTickTimeoutId: ReturnType<typeof setTimeout> | undefined;

      const scheduleMinuteTick = () => {
        if (abortController.signal.aborted) return;
        minuteTickTimeoutId = setTimeout(() => {
          if (abortController.signal.aborted) return;
          runInAction(() => {
            this.calendarQueryStartTime = DateTime.now();
          });
          scheduleMinuteTick();
        }, DateTime.now().endOf("minute").diffNow().milliseconds);
      };

      scheduleMinuteTick();

      const calendarQueryChangeDisposer = reaction(
        () =>
          [
            this.calendarQueryStartTime,
            this.store.spaceAccountSettings.calendar.showCalendarEventsWithNoAttendees,
          ] as const,
        () => {
          this.subscribeToCalendarEvents();
        }
      );

      return () => {
        abortController.abort();
        clearTimeout(minuteTickTimeoutId);
        reactionDisposer();
        calendarQueryChangeDisposer();
        this.liveQuerySubscription?.unsubscribe();
        this.calendarEventsSubscription?.unsubscribe();
      };
    });

    useEffectOnMount(() => {
      const subscription = liveQuery(() => this.store.noteSources.localTable.toArray()).subscribe({
        next: rows => {
          runInAction(() => {
            const uniqueIds = new Set<string>();
            for (const row of rows) {
              const noteId = row.model_data.note_id;
              if (noteId) uniqueIds.add(noteId);
            }
            this.notesWithSources = Array.from(uniqueIds);
          });
        },
      });

      return () => subscription.unsubscribe();
    });

    this.listState.useListStateEffects();
  }
}
